import {
  SET_HUB_ACTIVITY_LIKE,
  DELETE_HUB_ACTIVITY,
  DELETE_HUB_ACTIVITY_LIKE,
  // SET_HUB_ACTIVITY_REPLY_LIKE,
  // DELETE_HUB_ACTIVITY_REPLY,
  // DELETE_HUB_ACTIVITY_REPLY_LIKE,
  APPEND_ACTIVITY_REPLY,
} from "@/store/hubActivity-v1/hubActivityConstants";
import { WebSocketConnect, WebSocketConnectClass } from "../../socket";
import {
  SET_PROJECT_INVITATION_NOTIFICATIONS,
  SET_PROJECT_INVITATION_NOTIFICATION,
  SET_NOTIFICATION,
  SET_NOTIFICATIONS,
  SET_HUB_EVENT_INVITATION_NOTIFICATION,
  SET_HUB_EVENT_INVITATION_NOTIFICATIONS,
  SET_HUB_INVITATION_NOTIFICATION,
  SET_HUB_INVITATION_NOTIFICATIONS,
  UPDATE_HUB_EVENT_INVITATION_NOTIFICATION,
  UPDATE_HUB_INVITATION_NOTIFICATION,
  UPDATE_NOTIFICATION,
  UPDATE_PROJECT_INVITATION_NOTIFICATION,
} from "../notifications/notificationActions";
import {
  fetchActiveProjectInvitations,
  fetchHubInvitations,
  fetchHubEventInvitations,
} from "../invitations/invitationActions";
import { refreshAlertCounts } from "../alerts/alertsAction";
import type { List, Map as ImmutableMap } from "immutable";
import {
  // SET_HUB_ACTIVITY_ANNOUNCEMENT,
  // SET_HUB_ACTIVITY,
  // SET_HUB_ACTIVITY_POST,
  // SET_HUB_ACTIVITY_OFFER,
  // SET_HUB_ACTIVITY_IDEA,
  // SET_HUB_ACTIVITY_QUESTION,
  REMOVE_USER_HUB_CARD,
  SET_USER_HUB_CARD,
  SET_HUB_MEMBER,
  // SET_HUB_ACTIVITY_REPLY,
  fetchHubMembers,
  fetchUserHubCards,
} from "../hubs/hubActions";

// `phoenix` ships no type declarations and src/socket.jsx is untyped JS, so a
// channel arrives here as `any`. This is the subset of the channel API these
// thunks use. Event payloads stay `any` so each call site can annotate the shape
// it expects; the server is the only authority on those.
type ChannelPush = {
  receive: (status: string, callback: (payload: any) => void) => ChannelPush;
};

type PhoenixChannel = {
  join: () => ChannelPush;
  on: (event: string, callback: (payload: any) => void) => number;
  push: (event: string, payload: object) => ChannelPush;
};

// Channel events wrap their record in a `data` key, mirroring the Phoenix views.
type Envelope = { data: unknown };

type ProjectInvitationEvent = {
  notification: Envelope;
  project_invitation_notification: Envelope;
};

type HubEventInvitationEvent = {
  notification: Envelope;
  hub_event_invitation_notification: Envelope;
};

type HubInvitationEvent = {
  notification: Envelope;
  hub_invitation_notification: Envelope;
};

// The store is still plain JS, so there is no generated RootState. Each thunk
// declares only the slices it reads, which also keeps them cheap to call in
// tests. `any` values are the untyped store showing through, not a shortcut.
type UserMap = ImmutableMap<string, any>;

type ChannelsState = {
  rebelChannel: PhoenixChannel | null;
  hubChannels: ImmutableMap<number, PhoenixChannel>;
};

type Dispatch = (action: unknown) => unknown;

// REBEL USER CHANNEL
export const SET_REBEL_USER_CHANNEL = "channels/SET_REBEL_USER_CHANNEL";

// One user action can write more than one alert — a new project topic note
// fires both a type-3 and a type-5 — so pings arrive in bursts. Wait this long
// for the burst to finish before refetching.
const ALERT_REFETCH_DEBOUNCE_MS = 1000;

export function initializeRebelUserChannel() {
  return (dispatch: Dispatch, getState: () => { user: UserMap }) => {
    const { user } = getState();
    const channelName = WebSocketConnectClass.makeChannelName(
      WebSocketConnectClass.REBEL_USER_LOBBY,
      user.get("id")
    );
    const channel: PhoenixChannel = WebSocketConnect.channel(
      user.get("jwt"),
      channelName
    );

    let alertRefetchTimer: ReturnType<typeof setTimeout>;
    const refreshBadge = () => {
      clearTimeout(alertRefetchTimer);
      alertRefetchTimer = setTimeout(
        () => dispatch(refreshAlertCounts()),
        ALERT_REFETCH_DEBOUNCE_MS
      );
    };

    // init channel and save channel to store
    channel
      .join()
      .receive("ok", () => {
        dispatch({ type: SET_REBEL_USER_CHANNEL, data: channel });
        // Pings broadcast while the socket was down are gone — they carry no
        // payload and are never redelivered. Refreshing on every (re)join is
        // what closes that window.
        refreshBadge();
      })
      .receive("error", (error: unknown) => console.warn("error", error));

    // Payload-free invalidation ping, so the only thing to do is ask the server
    // again. It refreshes the badge counts alone: the list the user is looking
    // at stays put, and reopening the dropdown is what reloads it.
    channel.on("create:alert", refreshBadge);

    // fetch
    channel.push("fetch:notifications", {}).receive("ok", (payload: Envelope) => {
      dispatch({ type: SET_NOTIFICATIONS, data: payload.data });
    });

    channel
      .push("fetch:project_invitation_notifications", {})
      .receive("ok", (payload: Envelope) => {
        dispatch({
          type: SET_PROJECT_INVITATION_NOTIFICATIONS,
          data: payload.data,
        });
      });

    channel
      .push("fetch:hub_event_invitation_notifications", {})
      .receive("ok", (payload: Envelope) => {
        dispatch({
          type: SET_HUB_EVENT_INVITATION_NOTIFICATIONS,
          data: payload.data,
        });
      });

    channel
      .push("fetch:hub_invitation_notifications", {})
      .receive("ok", (payload: Envelope) => {
        dispatch({
          type: SET_HUB_INVITATION_NOTIFICATIONS,
          data: payload.data,
        });
      });

    // on create
    channel.on(
      "create:project_invitation_notification",
      (payload: ProjectInvitationEvent) => {
      dispatch(fetchActiveProjectInvitations());
      dispatch({ type: SET_NOTIFICATION, data: payload.notification.data });
      dispatch({
        type: SET_PROJECT_INVITATION_NOTIFICATION,
        data: payload.project_invitation_notification.data,
      });
    });

    channel.on(
      "create:hub_event_invitation_notification",
      (payload: HubEventInvitationEvent) => {
      dispatch(fetchHubEventInvitations());
      dispatch({ type: SET_NOTIFICATION, data: payload.notification.data });
      dispatch({
        type: SET_HUB_EVENT_INVITATION_NOTIFICATION,
        data: payload.hub_event_invitation_notification.data,
      });
    });

    channel.on(
      "create:hub_invitation_notification",
      (payload: HubInvitationEvent) => {
      dispatch(fetchHubInvitations());
      dispatch({ type: SET_NOTIFICATION, data: payload.notification.data });
      dispatch({
        type: SET_HUB_INVITATION_NOTIFICATION,
        data: payload.hub_invitation_notification.data,
      });
    });

    //on update
    channel.on(
      "update:project_invitation_notification",
      (payload: ProjectInvitationEvent) => {
      dispatch(fetchActiveProjectInvitations());
      dispatch({ type: UPDATE_NOTIFICATION, data: payload.notification.data });
      dispatch({
        type: UPDATE_PROJECT_INVITATION_NOTIFICATION,
        data: payload.project_invitation_notification.data,
      });
    });

    channel.on(
      "update:hub_event_invitation_notification",
      (payload: HubEventInvitationEvent) => {
      dispatch(fetchHubEventInvitations());
      dispatch({ type: UPDATE_NOTIFICATION, data: payload.notification.data });
      dispatch({
        type: UPDATE_HUB_EVENT_INVITATION_NOTIFICATION,
        data: payload.hub_event_invitation_notification.data,
      });
    });

    channel.on(
      "update:hub_invitation_notification",
      (payload: HubInvitationEvent) => {
      dispatch(fetchHubInvitations());
      dispatch({ type: UPDATE_NOTIFICATION, data: payload.notification.data });
      dispatch({
        type: UPDATE_HUB_INVITATION_NOTIFICATION,
        data: payload.hub_invitation_notification.data,
      });
    });
  };
}

// HUBS
export const SET_HUB_CHANNEL = "channels/SET_HUB_CHANNEL";

export function joinHubChannel(hubId: number) {
  return (
    dispatch: Dispatch,
    getState: () => { user: UserMap; channels: ChannelsState }
  ) => {
    const { user, channels } = getState();

    // check if already joined channel

    const hubChannel = channels.hubChannels.get(hubId);

    if (hubChannel) {
      return;
    }

    const channelName = WebSocketConnectClass.makeChannelName(
      WebSocketConnectClass.REBEL_HUB_ROOM_LOBBY,
      hubId
    );
    const channel = WebSocketConnect.channel(user.get("jwt"), channelName);

    channel
      .join()
      .receive("ok", () => dispatch({ type: SET_HUB_CHANNEL, hubId, channel }))
      .receive("error", (error: unknown) => console.warn("error", error));

    // user just joined hub - improve this
    channel.on("new_hub_member", () => {
      dispatch(fetchHubMembers());
      dispatch(fetchUserHubCards());
    });

    // hub role added or updated
    channel.on("user_hub_role_upserted", (payload: Envelope) => {
      dispatch({ type: SET_HUB_MEMBER, data: payload.data });
    });

    // user hub card created or updated
    channel.on("user_hub_card_upserted", (payload: Envelope) => {
      dispatch({ type: SET_USER_HUB_CARD, data: payload.data });
    });

    // user hub card deactivated
    channel.on(
      "user_hub_card_deactivated",
      (payload: { user_hub_card_id: number }) => {
      dispatch({
        type: REMOVE_USER_HUB_CARD,
        user_hub_card_id: payload.user_hub_card_id,
      });
    });

    // hub activity deleted
    channel.on("hub_activity_deleted", (payload: { hub_activity_id: number }) => {
      dispatch({
        type: DELETE_HUB_ACTIVITY,
        hubActivityId: payload.hub_activity_id,
      });
    });

    // // hub activity announcement created or updated
    // channel.on('hub_activity_announcement_upserted', (payload) => {
    //   dispatch({ type: SET_HUB_ACTIVITY, data: payload.hub_activity.data });
    //   dispatch({
    //     type: SET_HUB_ACTIVITY_ANNOUNCEMENT,
    //     data: payload.hub_activity_announcement.data,
    //   });
    // });

    // // hub activity post created or updated
    // channel.on('hub_activity_post_upserted', (payload) => {
    //   dispatch({ type: SET_HUB_ACTIVITY, data: payload.hub_activity.data });
    //   dispatch({
    //     type: SET_HUB_ACTIVITY_POST,
    //     data: payload.hub_activity_post.data,
    //   });
    // });

    // // hub activity offer created or updated
    // channel.on('hub_activity_offer_upserted', (payload) => {
    //   dispatch({ type: SET_HUB_ACTIVITY, data: payload.hub_activity.data });
    //   dispatch({
    //     type: SET_HUB_ACTIVITY_OFFER,
    //     data: payload.hub_activity_offer.data,
    //   });
    // });

    // // hub activity question created or updated
    // channel.on('hub_activity_question_upserted', (payload) => {
    //   dispatch({ type: SET_HUB_ACTIVITY, data: payload.hub_activity.data });
    //   dispatch({
    //     type: SET_HUB_ACTIVITY_QUESTION,
    //     data: payload.hub_activity_question.data,
    //   });
    // });

    // // hub activity idea created or updated
    // channel.on('hub_activity_idea_upserted', (payload) => {
    //   dispatch({ type: SET_HUB_ACTIVITY, data: payload.hub_activity.data });
    //   dispatch({
    //     type: SET_HUB_ACTIVITY_IDEA,
    //     data: payload.hub_activity_idea.data,
    //   });
    // });

    // hub activity like
    channel.on("hub_activity_liked", (payload: Envelope) => {
      dispatch({ type: SET_HUB_ACTIVITY_LIKE, payload: payload.data });
    });

    // hub activity unlike
    channel.on("hub_activity_unliked", (payload: Envelope) => {
      dispatch({ type: DELETE_HUB_ACTIVITY_LIKE, data: payload.data });
    });

    // hub activity reply created or updated
    channel.on("hub_activity_reply", (payload: Envelope) => {
      // dispatch({ type: SET_HUB_ACTIVITY_REPLY, data: payload.data });
      dispatch({ type: APPEND_ACTIVITY_REPLY, data: payload.data });
    });

    // hub activity reply deleted
    // channel.on('hub_activity_reply_deleted', (payload) => {
    //   dispatch({
    //     type: DELETE_HUB_ACTIVITY_REPLY,
    //     data: payload,
    //   });
    // });

    // hub activity reply like
    // channel.on('hub_activity_liked', (payload) => {
    //   dispatch({ type: SET_HUB_ACTIVITY_REPLY_LIKE, data: payload.data });
    // });

    // hub activity reply unlike
    // channel.on('hub_activity_unliked', (payload) => {
    //   dispatch({ type: DELETE_HUB_ACTIVITY_REPLY_LIKE, data: payload.data });
    // });
  };
}

export function joinHubChannels() {
  return (
    dispatch: Dispatch,
    getState: () => { hubs: { userHubs: List<UserMap> } }
  ) => {
    const { hubs } = getState();

    hubs.userHubs
      // .filter(event => moment.utc(event.get('end_date')).local().isAfter(moment()))
      .forEach((hub: UserMap) => dispatch(joinHubChannel(hub.get("id"))));
  };
}
